import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import ReactDOM from "react-dom";
import { Main } from "../../MUIStyled/Main";
import { useMediaQuery } from "@mui/material";
import { DrawerHeader } from "../../MUIStyled/DrawerHeader";
import { getNewLegendContent } from "../Widgets/legendContent";
import { photoLocations } from "../../../../config/360photos";
import { stormScenarios } from "../../../../config/stormScenarios";
import { criticalFacilityInitialState } from "../../../../lib/Helpers/criticalFacilityState";
import { criticalFacilitiesAssetLayers } from "../../../../lib/Helpers/criticalFacilitiesURLs";
import CustomImgPopup from "../../Photos/CustomImgPopup";
import ToggleFloodRenderings from "../../Photos/ToggleFloodRenderings";
import ToggleCriticalFacilities from "../Widgets/ToggleCriticalFacilities";
import WebMap from "@arcgis/core/WebMap";
import MapView from "@arcgis/core/views/MapView";
import Popup from "@arcgis/core/widgets/Popup.js";
import LayerList from "@arcgis/core/widgets/LayerList";
import * as reactiveUtils from "@arcgis/core/core/reactiveUtils.js";
import {
 addWidgets,
 createSlider,
 filterScenarios,
 updateLegendDiv,
 constructGallery,
 updateCurrSelection,
 initializeLayerList,
 updateResultantArray,
 generateSelectionText,
 addPhotoLocationsLayer,
 addStormScenarioLayers,
 addCriticalFacilitiesLayers,
} from "../../../../lib/Helpers/mapHelpers";
import "@photo-sphere-viewer/core/index.css";
import _ from "lodash";
import "../Maps.css";

// globals
let globalReductionState;
let photoLocationsLayerView;

const SecondMapComponent = ({
 open,
 checked,
 mapExtent,
 setMapExtent,
 reductionState,
 searchResultsFeature,
 criticalAssetsEnabled,
 appContentOpenClassname,
 setCriticalAssetsEnabled,
}) => {
 const mapRef = useRef();
 const criticalFacilitiesID2 = useRef("cf-component-2");
 const toggleFloodRenderingsID2 = useRef("flood-renderings-2");

 const isSmallScreen = useMediaQuery("(max-width: 460px)");

 const [theMap, setMap] = useState();
 const [mapView, setMapView] = useState();
 const [resultantArray, setResultantArray] = useState([]);
 const [photoLocationsLayer, setPhotoLocationsLayer] = useState();
 const [floodRenderingsVisible, setFloodRenderingsVisible] = useState(true); // toggle flood rendering visiblity
 const [criticalFacilitiesLayers, setCriticalFacilitiesLayers] = useState({
  ...criticalFacilityInitialState,
 });

 const scenarioTwo = useSelector((state) => state.scenarioTwo.scenarioTwo);
 const adaptation = useSelector(
  (state) => state.scenarioTwo.scenarioTwo["adaptation"]
 );
 const slr = useSelector((state) => state.scenarioTwo.scenarioTwo["slr"]);
 const rainfall = useSelector(
  (state) => state.scenarioTwo.scenarioTwo["rainfall"]
 );
 const surge = useSelector((state) => state.scenarioTwo.scenarioTwo["surge"]);
 const groundwater = useSelector(
  (state) => state.scenarioTwo.scenarioTwo["groundwater"]
 );

 // adaptationSelection
 useEffect(() => {
  const isAdaptation = true;
  updateResultantArray(
   reductionState["selectedAdaption"],
   reductionState["availableSLR"],
   setResultantArray,
   isAdaptation
  );
 }, [reductionState["selectedAdaption"], reductionState["availableSLR"]]);

 // slrSelection
 useEffect(() => {
  updateResultantArray(
   reductionState["selectedSLR"],
   reductionState["availableRain"],
   setResultantArray
  );
 }, [reductionState["selectedSLR"], reductionState["availableRain"]]);

 // rainfallSelection
 useEffect(() => {
  updateResultantArray(
   reductionState["selectedRainfallAmount"],
   reductionState["availableStormSurges"],
   setResultantArray
  );
 }, [
  reductionState["selectedRainfallAmount"],
  reductionState["availableStormSurges"],
 ]);

 // stormSurgeSelection
 useEffect(() => {
  updateResultantArray(
   reductionState["selectedStormSurge"],
   reductionState["availableGroundwater"],
   setResultantArray
  );
 }, [
  reductionState["selectedStormSurge"],
  reductionState["availableGroundwater"],
 ]);

 // groundwaterSelection
 useEffect(() => {
  updateResultantArray(
   reductionState["selectedGroundwaterConditions"],
   reductionState["finalSelection"],
   setResultantArray
  );
 }, [
  reductionState["selectedGroundwaterConditions"],
  reductionState["finalSelection"],
 ]);

 // set global reduction state variable...
 useEffect(() => {
  globalReductionState = { ...reductionState };
 }, [reductionState]);

 // toggle critical facility layers on and off
 const handleCriticalFacilityLayerChange = (layer, isVisible) => {
  if (!!layer) {
   layer.visible = isVisible;
  }
 };

 // create a popup template
 const getImage = (title) => {
  const popupTemplate = {
   title: `${title}`,
   content: (event) => {
    const container = document.createElement("div");
    container.className = "photo-360-container";

    const locationTitle = event.graphic.attributes["Name"];
    const reactContainer = document.createElement("div");

    // filter scenarios and construct the gallery
    const filteredScenarios = filterScenarios(scenarioTwo, stormScenarios);
    const gallery = constructGallery(
     locationTitle,
     filteredScenarios,
     photoLocations
    );

    // render the CustomImgPopup component into the reactContainer
    ReactDOM.render(
     <CustomImgPopup feature={event.graphic} newSRC={gallery} />,
     reactContainer
    );

    container.appendChild(reactContainer);
    return container;
   },
  };

  return popupTemplate;
 };

 // load the map, layers, and widgets
 useEffect(() => {
  if (mapRef.current) {
   const map = new WebMap({
    basemap: "gray-vector",
    popup: new Popup({
     defaultPopupTemplateEnabled: true,
    }),
   });
   setMap(map);

   const mapview = new MapView({
    container: mapRef.current,
    map: map,
    center: [-80.15, 26.15],
    zoom: 11,
    extent: mapExtent,
    popup: new Popup({
     defaultPopupTemplateEnabled: true,
    }),
   });
   setMapView(mapview);

   mapview.when(() => {
    // add layers
    addStormScenarioLayers(map, stormScenarios);
    addCriticalFacilitiesLayers(
     map,
     criticalFacilitiesAssetLayers,
     setCriticalFacilitiesLayers
    );
    addPhotoLocationsLayer(map, setPhotoLocationsLayer);

    // add opacity slider
    const slider = createSlider(mapview);

    // loop through the layers
    const layerList = new LayerList({
     view: mapview,
     listItemCreatedFunction: (event) => {
      var itemView = event.item; // layer-view of selection
      itemView.hidden = true;
      // olny show visible layers in the layerlist
      itemView.watch("visible", (event) => {
       layerList.operationalItems.forEach((layerView) => {
        if (
         layerView.title === globalReductionState?.finalSelection[0]?.["title"] // need global state variable here
        ) {
         layerView.hidden = false;
         // retain the transparency when switching between scenarios
         layerView.layer.opacity = slider.values[0];
         //  change opacity of visible layer
         reactiveUtils.watch(
          () => slider.values.map((value) => value),
          (values) => (layerView.layer.opacity = values[0])
         );
        } else {
         layerView.hidden = true;
        }
       });
      });
     },
    });

    // add custom widgets
    let criticalFacilitiesWidget = document.getElementById(
     criticalFacilitiesID2.current
    );
    let toggleFloodRenderingsWidget = document.getElementById(
     toggleFloodRenderingsID2.current
    );
    if (!!checked) {
     mapview.ui.add(criticalFacilitiesWidget, "top-right");
    }
    mapview.ui.add(toggleFloodRenderingsWidget, "top-right");

    const isMap1 = false; // flag to indicate this is NOT Map 1

    // add other widgets to the map view
    addWidgets(mapview, checked, "legendDiv2", "currSelection2", isMap1);
   });

   return () => {
    // find the critical facilities box, destroy it on component unmount
    let cfWidget = document.getElementById("cf-component-2");
    if (!!cfWidget) cfWidget.destroy();
    setCriticalAssetsEnabled(false);
    setCriticalFacilitiesLayers({
     ...criticalFacilityInitialState,
    });
    // find the flood renderings element, destroy it on component unmount
    let toggleFRWidget = document.getElementById("flood-renderings-2");
    if (!!toggleFRWidget) toggleFRWidget.destroy();
   };
  }
 }, []); // runs once

 // when the search results change in Map1, go to that point in Map2
 useEffect(() => {
  if (!!searchResultsFeature && !!mapView) {
   mapView.graphics.add(searchResultsFeature);
   mapView
    .goTo(searchResultsFeature.geometry)
    .catch((err) => console.log({ err }));
  }
  if (!!!searchResultsFeature && !!mapView) {
   //  mapView.graphics.items.forEach((item) => item.destroy());
   mapView.graphics.destroy();
   mapView.graphics.items = null;
  }
 }, [searchResultsFeature]);

 // show the citical facilities layers - no login required
 useEffect(() => {
  if (!!mapRef.current && !!theMap) {
   if (!!criticalAssetsEnabled && !!checked) {
    Object.keys(criticalFacilityInitialState)?.forEach((stateVar) => {
     if (!!criticalFacilitiesLayers[stateVar]) {
      // add the map layers
      theMap.add(criticalFacilitiesLayers[stateVar]);
      // turn layers on
      handleCriticalFacilityLayerChange(
       criticalFacilitiesLayers[stateVar],
       true
      );
     }
    });
   }
  }
 }, [criticalAssetsEnabled, theMap, checked]);

 // match the extents for map 1 and map 2
 useEffect(() => {
  let firstRun = true;
  if (!!theMap) {
   // look for when the mapview is stationary
   // Equivalent to watchUtils.whenTrue()
   reactiveUtils.when(
    () => mapView?.stationary === true,
    async () => {
     if (firstRun) {
      firstRun = false;
     } else {
      // set the store with the new extent information
      setMapExtent(mapView.extent);
     }
    }
   );
  }
 }, [theMap]);

 // make the map extents in both maps match
 useEffect(() => {
  if (!!theMap) {
   if (
    !!mapView &&
    !!mapView.extent &&
    !!!mapView.extent.equals(mapExtent) &&
    !!checked
   ) {
    mapView.extent = mapExtent;
   }
  }
 }, [mapExtent, theMap]);

 // change the layer list when the resultant array changes
 useEffect(() => {
  if (mapRef.current && !!mapView && !!photoLocationsLayer) {
   reactiveUtils
    .whenOnce(() => mapView.ready)
    .then(() => {
     initializeLayerList(
      mapView,
      resultantArray,
      floodRenderingsVisible,
      (layerView) => {
       photoLocationsLayerView = layerView; // set global layer variable
      }
     );
    });
  }
 }, [theMap, checked, resultantArray, photoLocationsLayerView]);

 // update the current selection box/widget
 useEffect(() => {
  if (!!mapRef.current && !!mapView) {
   const currSelectionBox = document.querySelectorAll("#currSelection2");
   const selectionText = generateSelectionText(reductionState, slr, rainfall);
   updateCurrSelection(currSelectionBox, selectionText);
  }
 }, [
  theMap,
  rainfall,
  slr,
  reductionState["selectedSLR"],
  reductionState["selectedAdaptation"],
  reductionState["selectedStormSurge"],
  reductionState["selectedRainfallAmount"],
  reductionState["selectedGroundwaterConditions"],
 ]);

 // update the legend widget
 useEffect(() => {
  if (!!mapRef.current && !!mapView) {
   const legendDivBox = document.querySelectorAll("#legendDiv2");
   const legendContent = getNewLegendContent(resultantArray);
   updateLegendDiv(legendDivBox, legendContent);
  }
 }, [mapView, resultantArray]);

 // on click, show critical facility/360 photo popups
 useEffect(() => {
  if (!!mapView && !!theMap && !!mapRef.current) {
   const handleClick = (event) => {
    mapView.hitTest(event).then((response) => {
     response.results
      .filter((result) => {
       const title = result?.layer?.title || "";
       return title.includes("360") || title.includes("Critical");
      })
      .forEach((result) => {
       if (result.layer.title.includes("360")) {
        const title = result.graphic.attributes.Name;
        result.graphic.layer.popupTemplate = getImage(title); // create a popup template to show 360 photos
       } else if (result.layer.title.includes("Critical")) {
        const newTitle = result.layer.defaultPopupTemplate.title.replace(
         "Broward County Critical Assets - ",
         ""
        );
        result.layer.defaultPopupTemplate.title = newTitle;
       }
      });
    });
   };

   mapView.on("click", handleClick);

   //  return () => {
   //   mapView.off("click", handleClick); // cleanup the event listener
   //  };
  }
 }, [theMap, mapView]);

 const iOS =
  typeof navigator !== "undefined" &&
  /iPad|iPhone|iPod/.test(navigator.userAgent);

 return (
  <>
   {!!iOS === true || !!isSmallScreen ? (
    <Main open={open} className="app-content-ios">
     <DrawerHeader />
     <div className="webmap2">
      <div className={`webmap`} ref={mapRef} />
     </div>
    </Main>
   ) : (
    <Main
     className={
      open === true
       ? `${appContentOpenClassname}`
       : "app-content-closed-compare"
     }
     open={open}
    >
     <DrawerHeader />
     <div className="webmap2">
      <div className={`webmap`} ref={mapRef}>
       <ToggleCriticalFacilities
        id={criticalFacilitiesID2.current}
        handleCriticalFacilityLayerChange={handleCriticalFacilityLayerChange}
        criticalAssetsEnabled={criticalAssetsEnabled}
        setCriticalAssetsEnabled={setCriticalAssetsEnabled}
        criticalFacilitiesLayers={criticalFacilitiesLayers}
       />
       <ToggleFloodRenderings
        id={toggleFloodRenderingsID2.current}
        photoLocationsLayer={photoLocationsLayer}
        floodRenderingsVisible={floodRenderingsVisible}
        setFloodRenderingsVisible={setFloodRenderingsVisible}
       />
      </div>
     </div>
    </Main>
   )}
  </>
 );
};

export default SecondMapComponent;
